/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useCallback, useState } from 'react';

import { Button, InlineNotification } from '@coursera/cds-core';
import type { ButtonProps } from '@coursera/cds-core';

import PasswordConfirmationModal from 'bundles/account-settings/components/sections/session-invalidation/PasswordConfirmationModal';
import * as actions from 'bundles/authentication/shared/actions';
import withSingleTracked from 'bundles/common/components/withSingleTracked';

import _t from 'i18n!nls/account-settings';

const TrackedButton = withSingleTracked({ type: 'BUTTON' })<ButtonProps>(Button);

const styles = {
  // Styles are unsetting this.
  button: css`
    box-shadow: inset 0 0 0 1px var(--cds-color-interactive-primary);
  `,
};

const SessionInvalidation = () => {
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [modal, setModal] = useState(false);

  const close = useCallback<() => $TSFixMe>(() => {
    setModal(false);
  }, [setModal]);

  const open = useCallback<() => $TSFixMe>(() => {
    setModal(true);
  }, [setModal]);

  const invalidate = useCallback<(...args: $TSFixMe[]) => $TSFixMe>(
    async (password: string) => {
      try {
        setError('');
        setSuccess(false);

        await actions.invalidate({ password });

        setSuccess(true);
      } catch (e) {
        const ERRORS = {
          401: _t('Password does not match our records.'),
          500: _t('Oops! Something went wrong, try later'),
        };

        setError(ERRORS[e.status as keyof typeof ERRORS] ?? ERRORS[500]);
      }

      close();
    },
    [close, setError]
  );

  return (
    <section>
      {modal && (
        <PasswordConfirmationModal
          description={_t(
            'Enter your Coursera password to confirm you want to log out of your account on other devices.'
          )}
          isOpen={modal}
          onCancel={close}
          onConfirm={invalidate}
          submit={_t('Confirm logout')}
          title={_t('Enter your password')}
        />
      )}

      <h2 className="section-title">{_t('Connected devices')}</h2>

      <p>{_t('If your account has been logged into on multiple devices, you can log out from here.')}</p>

      <div className="bt3-row">
        <div className="bt3-col-md-12 horizontal-box">
          <TrackedButton variant="primaryInvert" css={styles.button} onClick={open} trackingName="invalidate_session">
            {_t('Log out from all devices')}
          </TrackedButton>
          {error && <div className="rc-StatusMessage error m-t-1s">{error}</div>}
          {success && (
            <InlineNotification className="rc-StatusMessage" severity="success" role="alert">
              {_t('You successfully logged out from the rest of your devices.')}
            </InlineNotification>
          )}
        </div>
      </div>
    </section>
  );
};

export default SessionInvalidation;
